import { useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  KeyRound, Plus, Pencil, Trash2, FlaskConical, CheckCircle2, XCircle, Loader2, Vault, Power, Boxes, ChevronDown, ChevronUp,
  LayoutGrid, List as ListIcon,
} from 'lucide-react';
import { ProviderIcon, PROVIDER_LABEL } from '@/components/products/ProviderIcon';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import {
  productCredentialsService, Credential, CredentialInput, CredentialProvider, KNOWN_ENV_NAMES,
} from '@/services/product-credentials.service';

const PROVIDERS: Array<{ id: CredentialProvider; label: string }> = [
  { id: 'openai', label: 'OpenAI' }, { id: 'anthropic', label: 'Anthropic (Claude)' }, { id: 'gemini', label: 'Google Gemini' },
  { id: 'qwen', label: 'Qwen / DashScope' }, { id: 'grok', label: 'xAI Grok' }, { id: 'elevenlabs', label: 'ElevenLabs' },
  { id: 'mistral', label: 'Mistral' }, { id: 'imagekit', label: 'ImageKit' }, { id: 'aws', label: 'AWS (S3, CloudFront)' }, { id: 'other', label: 'Other (payments, anything)' },
];

const relative = (iso?: string | null) => {
  if (!iso) return 'never';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${Math.max(1, m)} min ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/**
 * The vault: every secret the operator's apps need, stored once, assigned per
 * product. Apps fetch their assigned set at startup. The CRM never proxies
 * the calls — it hands out keys and can take them back.
 */
export function CredentialsVault({ onChanged }: { onChanged?: () => void }) {
  const [rows, setRows] = useState<Credential[] | null>(null);
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState<Credential | 'new' | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [view, setView] = useState<'grid' | 'list'>(() => {
    try { return (localStorage.getItem('vault:view') as 'grid' | 'list') || 'grid'; } catch { return 'grid'; }
  });
  const switchView = (v: 'grid' | 'list') => { setView(v); try { localStorage.setItem('vault:view', v); } catch { /* ignore */ } };

  const load = () => productCredentialsService.list().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const testResult = (c: Credential) => (c.lastTestResult ? { ok: c.lastTestResult === 'ok' } : null);

  const test = async (c: Credential) => {
    setBusy(`test:${c.credentialUid}`);
    try { const r = await productCredentialsService.test(c.credentialUid); (r.ok ? toast.success : toast.error)(`${c.label}: ${r.detail}`); load(); }
    catch { /* interceptor */ } finally { setBusy(null); }
  };
  const toggle = async (c: Credential) => {
    setBusy(c.credentialUid);
    try { await productCredentialsService.update(c.credentialUid, { isActive: !c.isActive }); toast.success(c.isActive ? 'Disabled — apps stop receiving it on next fetch' : 'Enabled'); load(); onChanged?.(); }
    catch { /* interceptor */ } finally { setBusy(null); }
  };
  const remove = async (c: Credential) => {
    const using = c.usedBy.length ? ` It is assigned to ${c.usedBy.map((p) => p.name).join(', ')}, which will stop receiving it.` : '';
    if (!window.confirm(`Delete ${c.label} (${c.envName})?${using}`)) return;
    setBusy(c.credentialUid);
    try { await productCredentialsService.remove(c.credentialUid); toast.success('Deleted'); load(); onChanged?.(); }
    catch { /* interceptor */ } finally { setBusy(null); }
  };

  return (
    <section className="rounded-2xl border border-border bg-surface shadow-soft">
      <header className="flex items-center gap-3 px-5 py-4">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-soft text-primary"><Vault size={16} /></span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-text-primary">Credentials vault</h2>
          <p className="text-2xs text-text-muted">
            Every key your apps need — AI providers, AWS, anything from a <code className="font-mono">.env</code> — stored once here and assigned per product.
            Apps pull their set at startup; rotate here, restart the app.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {rows && <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs text-text-muted">{rows.length} key{rows.length === 1 ? '' : 's'}</span>}
          <div className="flex rounded-lg border border-border p-0.5">
            {([['grid', <LayoutGrid size={13} />], ['list', <ListIcon size={13} />]] as Array<['grid' | 'list', React.ReactNode]>).map(([v, ic]) => (
              <button key={v} onClick={() => switchView(v)} className={clsx('rounded-md p-1.5 transition-colors', view === v ? 'bg-primary-soft text-primary' : 'text-text-muted hover:text-text-primary')} aria-label={`${v} view`}>{ic}</button>
            ))}
          </div>
          <Button size="sm" onClick={() => setEditing('new')} iconLeft={<Plus size={13} />}>Add key</Button>
          <button onClick={() => setOpen((v) => !v)} className="rounded-lg p-1.5 text-text-muted hover:bg-surface-alt hover:text-text-primary" aria-label={open ? 'Collapse' : 'Expand'}>
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </header>

      {open && (
        rows === null ? (
          <div className="flex items-center gap-2 border-t border-border px-5 py-6 text-sm text-text-muted"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="border-t border-border px-5 py-8 text-center">
            <KeyRound size={22} className="mx-auto text-text-muted" />
            <div className="mt-2 text-sm font-medium text-text-primary">No keys stored yet</div>
            <div className="mt-0.5 text-2xs text-text-muted">Add the keys from your apps' .env files, then assign them to each product.</div>
          </div>
        ) : view === 'grid' ? (
          <div className="grid gap-3 border-t border-border p-5 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
            {rows.map((c) => {
              const t = testResult(c);
              return (
                <div key={c.credentialUid} className={clsx('flex flex-col rounded-2xl border border-border bg-surface p-4 transition-shadow hover:shadow-md', !c.isActive && 'opacity-60')}>
                  <div className="flex items-start gap-3">
                    <ProviderIcon provider={c.provider} size={40} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-text-primary">{c.label}</div>
                      <div className="truncate text-2xs text-text-muted">{PROVIDER_LABEL[c.provider]}</div>
                    </div>
                    {!c.isActive && <span className="rounded-full bg-surface-alt px-1.5 py-0.5 text-2xs text-text-muted">off</span>}
                  </div>
                  <div className="mt-3 rounded-lg bg-surface-alt/60 px-2.5 py-1.5 font-mono text-2xs">
                    <div className="truncate text-text-primary">{c.envName}</div>
                    <div className="flex items-center justify-between text-text-muted"><span>{c.maskedValue}</span>{Object.keys(c.extras).length > 0 && <span>+{Object.keys(c.extras).length} extra{Object.keys(c.extras).length === 1 ? '' : 's'}</span>}</div>
                  </div>
                  <div className="mt-3 flex items-center gap-1.5 text-2xs">
                    <Boxes size={11} className="shrink-0 text-text-muted" />
                    {c.usedBy.length === 0
                      ? <span className="text-warning">Not assigned to any product</span>
                      : <span className="truncate text-text-secondary">{c.usedBy.map((p) => p.name).join(', ')}</span>}
                  </div>
                  {t && <div className={clsx('mt-1 inline-flex items-center gap-1 text-2xs', t.ok ? 'text-success' : 'text-danger')} title={c.lastTestDetail}>{t.ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />} {t.ok ? 'Key works' : 'Test failed'} · {relative(c.lastTestedAt)}</div>}
                  <div className="mt-3 flex items-center justify-between border-t border-border pt-2.5">
                    <div className="flex items-center gap-0.5">
                      {c.provider !== 'other' && <IconBtn title="Test key" onClick={() => test(c)} disabled={!!busy}>{busy === `test:${c.credentialUid}` ? <Loader2 size={13} className="animate-spin" /> : <FlaskConical size={13} />}</IconBtn>}
                      <IconBtn title={c.isActive ? 'Disable' : 'Enable'} onClick={() => toggle(c)} disabled={!!busy}><Power size={13} /></IconBtn>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <IconBtn title="Edit / rotate" onClick={() => setEditing(c)} disabled={!!busy}><Pencil size={13} /></IconBtn>
                      <IconBtn title="Delete" danger onClick={() => remove(c)} disabled={!!busy}><Trash2 size={13} /></IconBtn>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="border-t border-border">
            <table className="w-full text-sm">
              <thead className="bg-surface-alt/40 text-2xs uppercase tracking-wide text-text-muted">
                <tr>
                  <th className="px-5 py-2.5 text-left font-semibold">Key</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Variable</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Assigned to</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Status</th>
                  <th className="px-5 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((c) => {
                  const t = testResult(c);
                  return (
                    <tr key={c.credentialUid} className={clsx('hover:bg-surface-alt/30', !c.isActive && 'opacity-60')}>
                      <td className="px-5 py-2.5">
                        <div className="flex items-center gap-3">
                          <ProviderIcon provider={c.provider} size={30} />
                          <div className="min-w-0"><div className="truncate font-medium text-text-primary">{c.label}</div><div className="text-2xs text-text-muted">{PROVIDER_LABEL[c.provider]}</div></div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-2xs"><div className="text-text-primary">{c.envName}</div><div className="text-text-muted">{c.maskedValue}{Object.keys(c.extras).length > 0 && ` · +${Object.keys(c.extras).length}`}</div></td>
                      <td className="px-3 py-2.5 text-2xs">{c.usedBy.length === 0 ? <span className="text-warning">not assigned</span> : <span className="text-text-secondary">{c.usedBy.map((p) => p.name).join(', ')}</span>}</td>
                      <td className="px-3 py-2.5 text-2xs">
                        {!c.isActive ? <span className="rounded-full bg-surface-alt px-2 py-0.5 text-text-muted">disabled</span>
                          : t ? <span className={clsx('inline-flex items-center gap-1', t.ok ? 'text-success' : 'text-danger')} title={c.lastTestDetail}>{t.ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />} {t.ok ? 'works' : 'failed'} · {relative(c.lastTestedAt)}</span>
                            : <span className="text-text-muted">active · untested</span>}
                      </td>
                      <td className="px-5 py-2.5">
                        <div className="flex items-center justify-end gap-0.5">
                          {c.provider !== 'other' && <IconBtn title="Test key" onClick={() => test(c)} disabled={!!busy}>{busy === `test:${c.credentialUid}` ? <Loader2 size={13} className="animate-spin" /> : <FlaskConical size={13} />}</IconBtn>}
                          <IconBtn title={c.isActive ? 'Disable' : 'Enable'} onClick={() => toggle(c)} disabled={!!busy}><Power size={13} /></IconBtn>
                          <IconBtn title="Edit / rotate" onClick={() => setEditing(c)} disabled={!!busy}><Pencil size={13} /></IconBtn>
                          <IconBtn title="Delete" danger onClick={() => remove(c)} disabled={!!busy}><Trash2 size={13} /></IconBtn>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {editing && (
        <CredentialEditor
          credential={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); onChanged?.(); }}
        />
      )}
    </section>
  );
}

function CredentialEditor({ credential, onClose, onSaved }: { credential: Credential | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<CredentialInput>({
    envName: credential?.envName ?? '',
    label: credential?.label ?? '',
    provider: credential?.provider ?? 'openai',
    value: '',
    extras: credential?.extras ?? {},
  });
  const [extraRows, setExtraRows] = useState<Array<{ k: string; v: string }>>(
    Object.entries(credential?.extras ?? {}).map(([k, v]) => ({ k, v })),
  );
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof CredentialInput>(k: K, v: CredentialInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  const pickKnown = (name: string) => {
    const known = KNOWN_ENV_NAMES.find((k) => k.name === name);
    set('envName', name);
    if (known) { set('provider', known.provider); if (!form.label) set('label', known.name.replace(/_API_KEY$/, '').replace(/_/g, ' ')); }
  };

  const save = async () => {
    const envName = form.envName.trim().toUpperCase();
    if (!/^[A-Z][A-Z0-9_]{2,63}$/.test(envName)) { toast.error('Variable name must look like OPENAI_API_KEY'); return; }
    if (!form.label.trim()) { toast.error('Give it a label'); return; }
    if (!credential && !form.value.trim()) { toast.error('Paste the key value'); return; }
    const extras: Record<string, string> = {};
    for (const r of extraRows) if (r.k.trim() && r.v.trim()) extras[r.k.trim().toUpperCase()] = r.v.trim();
    setBusy(true);
    try {
      if (credential) {
        await productCredentialsService.update(credential.credentialUid, { label: form.label.trim(), provider: form.provider, extras, ...(form.value.trim() ? { value: form.value.trim() } : {}) });
        toast.success(form.value.trim() ? 'Key rotated — restart the apps that use it' : 'Saved');
      } else {
        await productCredentialsService.create({ envName, label: form.label.trim(), provider: form.provider, value: form.value.trim(), extras });
        toast.success('Key stored — now assign it to a product');
      }
      onSaved();
    } catch { /* interceptor */ } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={() => !busy && onClose()} title={credential ? `Edit ${credential.label}` : 'Add a key to the vault'} widthClass="max-w-xl">
      <div className="space-y-4">
        <FormField label="Environment variable name" required hintBelow hint={credential ? 'Fixed — apps read this exact name.' : 'Exactly as the app reads it from .env. Pick a suggestion or type any name.'}>
          <Input list="known-env-names" value={form.envName} disabled={!!credential} onChange={(e) => pickKnown(e.target.value.toUpperCase())} placeholder="OPENAI_API_KEY" className="font-mono" autoFocus />
          <datalist id="known-env-names">{KNOWN_ENV_NAMES.map((k) => <option key={k.name} value={k.name}>{k.hint}</option>)}</datalist>
        </FormField>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Label" required><Input value={form.label} onChange={(e) => set('label', e.target.value)} placeholder="OpenAI · PrepMDCAT" /></FormField>
          <FormField label="Provider" hintBelow hint="Enables the live Test button.">
            <div className="flex items-center gap-2">
              <ProviderIcon provider={form.provider ?? 'other'} size={38} />
              <Select value={form.provider} onChange={(e) => set('provider', e.target.value as CredentialProvider)}>
                {PROVIDERS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </Select>
            </div>
          </FormField>
        </div>
        <FormField label={credential ? 'New value (leave empty to keep the current one)' : 'Value'} required={!credential} hintBelow hint="Stored encrypted. Shown masked afterwards; never returned to the browser again.">
          <Input type="password" value={form.value} onChange={(e) => set('value', e.target.value)} placeholder={credential ? credential.maskedValue : 'sk-…'} className="font-mono" />
        </FormField>
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-sm font-medium text-text-primary">Companion variables <span className="text-2xs font-normal text-text-muted">(optional, not secret)</span></span>
            <button type="button" onClick={() => setExtraRows((r) => [...r, { k: '', v: '' }])} className="text-2xs text-primary hover:underline">+ add</button>
          </div>
          <p className="mb-2 text-2xs text-text-muted">Delivered together with the key — e.g. <code className="font-mono">OPENAI_BASE_URL</code>, <code className="font-mono">ELEVENLABS_VOICE_ID</code>, <code className="font-mono">AWS_REGION</code>.</p>
          <div className="space-y-1.5">
            {extraRows.map((r, i) => (
              <div key={i} className="flex gap-2">
                <Input value={r.k} onChange={(e) => setExtraRows((rows) => rows.map((x, j) => (j === i ? { ...x, k: e.target.value.toUpperCase() } : x)))} placeholder="OPENAI_BASE_URL" className="font-mono" />
                <Input value={r.v} onChange={(e) => setExtraRows((rows) => rows.map((x, j) => (j === i ? { ...x, v: e.target.value } : x)))} placeholder="https://api.openai.com/v1" className="font-mono" />
                <button type="button" onClick={() => setExtraRows((rows) => rows.filter((_, j) => j !== i))} className="rounded-lg px-2 text-text-muted hover:text-danger"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={save} disabled={busy}>{busy ? 'Saving…' : credential ? 'Save' : 'Store key'}</Button>
      </div>
    </Modal>
  );
}


function IconBtn({ children, title, onClick, disabled, danger }: { children: React.ReactNode; title: string; onClick: () => void; disabled?: boolean; danger?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} aria-label={title}
      className={clsx('rounded-lg p-1.5 text-text-muted transition-colors disabled:opacity-50', danger ? 'hover:bg-danger-soft hover:text-danger' : 'hover:bg-surface-alt hover:text-text-primary')}>
      {children}
    </button>
  );
}
